/** Helpers for showing Google Calendar events in week and month horizon cells. */
import { PLANNING_REGIMES } from '@/features/planning/config/planningConfig';

/**
 * All-day GCal events use `start.date` (YYYY-MM-DD) and no `dateTime`.
 * @param {object} event
 */
export const isGCalAllDayEvent = (event) =>
  Boolean(event?.start?.date) && !event?.start?.dateTime;

const padTime = (value) => String(value).padStart(2, '0');

/**
 * @param {object} event
 * @returns {Date}
 */
export const getGCalEventStartDate = (event) => {
  if (isGCalAllDayEvent(event)) {
    const [year, month, day] = event.start.date.split('-').map(Number);
    return new Date(year, month - 1, day);
  }
  return new Date(event.start.dateTime || event.start.date);
};

/**
 * @param {object} event
 * @returns {string} HH:mm
 */
export const getGCalEventClockTime = (event) => {
  const start = getGCalEventStartDate(event);
  return `${padTime(start.getHours())}:${padTime(start.getMinutes())}`;
};

/**
 * @param {object} event
 * @param {Date} date
 */
export const gcalEventFallsOnDay = (event, date) => {
  if (!event?.start || !date) return false;
  const start = getGCalEventStartDate(event);
  return (
    start.getFullYear() === date.getFullYear() &&
    start.getMonth() === date.getMonth() &&
    start.getDate() === date.getDate()
  );
};

/**
 * @param {object} event
 * @param {string} timeSlot
 * @param {{ start: string, end: string }|null} regime
 * @param {{ start: string, end: string }[]} [regimes]
 */
export const gcalEventMatchesTimeSlot = (
  event,
  timeSlot,
  regime,
  regimes = PLANNING_REGIMES,
) => {
  const allDay = isGCalAllDayEvent(event);
  if (timeSlot === 'allday') return allDay;
  if (allDay || !regime) return false;

  const time = getGCalEventClockTime(event);
  if (time >= regime.start && time < regime.end) return true;

  const index = regimes.findIndex(
    (item) => item.start === regime.start && item.end === regime.end,
  );
  if (index === 0 && time < regime.start) return true;
  if (index === regimes.length - 1 && time >= regime.end) return true;
  return false;
};

/**
 * @param {object[]} events
 * @param {Date} date
 */
export const filterGCalEventsForDay = (events, date) =>
  (events || []).filter((event) => gcalEventFallsOnDay(event, date));

/**
 * @param {object[]} events
 * @param {string} timeSlot
 * @param {{ start: string, end: string }|null} regime
 */
export const filterGCalEventsForTimeSlot = (events, timeSlot, regime) =>
  (events || []).filter((event) =>
    gcalEventMatchesTimeSlot(event, timeSlot, regime),
  );
